import { useCallback, useMemo, useState } from 'react';
import { AlertTriangle, CheckCircle2, Loader2, RotateCw } from 'lucide-react';
import { toast } from 'sonner';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { quotationService } from '../../services/quotationService';
import { useQuotationDraftStore } from '../../store/quotationDraftStore';
import { EmailExtractionStatus, type QuotationDraft } from '../../types/quotation.types';

interface ReviewExtractionHeaderProps {
  draft: QuotationDraft;
}

// Slim header above the line-items list inside the review panel for AutoDraft quotations.
// CSC-283 Layer 3: passes the multi-select attachment set from the source panel through
// to retry-extraction so sales can consolidate multiple sources (e.g. 2 photos of one
// RFQ across pages) into a single combined Gemini call.
export function ReviewExtractionHeader({ draft }: ReviewExtractionHeaderProps) {
  const status = draft.emailExtractionStatus;
  const selectedIds = useQuotationDraftStore((s) => s.reviewSelectedAttachmentIds);
  const loadDraft = useQuotationDraftStore((s) => s.loadDraft);
  const setEmailExtractionStatus = useQuotationDraftStore((s) => s.setEmailExtractionStatus);
  const [isRetrying, setIsRetrying] = useState(false);

  // The default selection (when sales hasn't toggled anything explicitly) mirrors the
  // backend default: all real (non-signature) attachments. Materialized here so the
  // confirmation messages can show the correct count.
  const defaults = useMemo(
    () =>
      draft.sourceEmail?.attachments
        .filter((a) => !a.isLikelySignature)
        .map((a) => a.id) ?? [],
    [draft.sourceEmail],
  );
  const effectiveSelectedIds = selectedIds ?? defaults;

  const hasExistingLines = (draft.lineItems?.length ?? 0) > 0;

  const performRetry = useCallback(async () => {
    if (draft.serverId == null) return;
    setIsRetrying(true);
    // CSC-291: optimistically flip the persisted status to Pending so the master-list
    // row spinner + detail-panel badge stay visible across navigation while the call
    // is in flight. loadDraft(updated) overwrites with the server's verdict on success;
    // catch sets Failed locally so a navigated-away retry surfaces correctly on return.
    setEmailExtractionStatus(draft.id, EmailExtractionStatus.Pending);
    try {
      // Send the explicit set when sales has toggled something, OR when the default set
      // would be ambiguous (multi-source) so the backend doesn't fall back to its own
      // default and reject. When the set matches the default exactly, send null so the
      // backend can apply its own logic (and our default behaviour stays consistent).
      const payload =
        selectedIds == null ? undefined : effectiveSelectedIds;
      const updated = await quotationService.retryExtraction(draft.serverId, payload);
      loadDraft(updated);
      toast.success(
        updated.lineItems.length > 0
          ? `Extracted ${updated.lineItems.length} line item${updated.lineItems.length === 1 ? '' : 's'}`
          : 'Extraction completed',
      );
    } catch (err) {
      const msg = err instanceof Error ? err.message : 'Retry failed';
      toast.error(msg);
      setEmailExtractionStatus(draft.id, EmailExtractionStatus.Failed);
    } finally {
      setIsRetrying(false);
    }
  }, [draft.id, draft.serverId, effectiveSelectedIds, loadDraft, selectedIds, setEmailExtractionStatus]);

  const handleRetry = useCallback(() => {
    if (isRetrying || draft.serverId == null) return;

    // Warn-before-replace (CSC-283 Layer 3): line items get replaced wholesale on every
    // retry. If sales has manually edited / added lines after the previous extraction,
    // re-running silently throws their work away. Confirm before clobbering.
    if (hasExistingLines) {
      const confirmed = window.confirm(
        `Re-running extraction will REPLACE the current ${draft.lineItems.length} line item(s) on this draft.\n\n` +
          `Sources to extract from: ${effectiveSelectedIds.length} attachment(s) + email body.\n\n` +
          'Any manual edits to the line items will be lost. Continue?',
      );
      if (!confirmed) return;
    }
    void performRetry();
  }, [draft.lineItems.length, draft.serverId, effectiveSelectedIds.length, hasExistingLines, isRetrying, performRetry]);

  const sourceCountLabel =
    effectiveSelectedIds.length === 0
      ? 'body only'
      : `${effectiveSelectedIds.length} attachment${effectiveSelectedIds.length === 1 ? '' : 's'} + body`;

  return (
    <div className="flex items-center justify-between gap-2 border-b bg-muted/30 px-3 py-2">
      <div className="flex items-center gap-2">
        <span className="text-xs font-medium uppercase tracking-wide text-muted-foreground">
          Extraction
        </span>
        <StatusBadge status={status} />
        <span className="text-xs text-muted-foreground">· {sourceCountLabel}</span>
      </div>
      <Button
        size="sm"
        variant="outline"
        onClick={handleRetry}
        disabled={isRetrying}
        className="h-7"
      >
        {isRetrying ? (
          <>
            <Loader2 className="mr-1.5 h-3 w-3 animate-spin" />
            Retrying…
          </>
        ) : (
          <>
            <RotateCw className="mr-1.5 h-3 w-3" />
            Retry extraction
          </>
        )}
      </Button>
    </div>
  );
}

function StatusBadge({ status }: { status: EmailExtractionStatus }) {
  switch (status) {
    case EmailExtractionStatus.Completed:
      return (
        <Badge variant="success" className="gap-1 text-xs">
          <CheckCircle2 className="h-3 w-3" />
          Completed
        </Badge>
      );
    case EmailExtractionStatus.SkippedAmbiguous:
      return (
        <Badge variant="secondary" className="gap-1 text-xs">
          <AlertTriangle className="h-3 w-3" />
          Skipped (ambiguous)
        </Badge>
      );
    case EmailExtractionStatus.Failed:
      return (
        <Badge variant="destructive" className="gap-1 text-xs">
          <AlertTriangle className="h-3 w-3" />
          Failed
        </Badge>
      );
    case EmailExtractionStatus.Pending:
      return (
        <Badge variant="outline" className="gap-1 text-xs">
          <Loader2 className="h-3 w-3 animate-spin" />
          Pending
        </Badge>
      );
    default:
      return (
        <Badge variant="outline" className="text-xs">
          —
        </Badge>
      );
  }
}
